import React from 'react';
import "./MapboxMap.scss"
import { useEffect, useRef, useState, useMemo } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl, { GeoJSONSource } from 'mapbox-gl';
import { useTheme } from '@mui/material';
import * as mapUtils from '../../map-utils';
import { IMapData, ICoordinates, IMapLayerData, IMapLayer, MapLayerTypes } from '../../../../shared/models/map_models';
import { UserSettings } from '../../../../shared/constants/user_constants';
import { useLocalStorageWatcher } from '../../../../shared/components/localStorageWatcher';
import { Feature, FeatureCollection, Polygon } from 'geojson';
import { GeoJSONFeatureCollection, IMapLocation } from '../../map-models';
import debounce from 'lodash/debounce';
import { AppDispatch } from '../../../../shared/store';
import { useDispatch } from 'react-redux';
import { setLayerFeatureCollection } from '../../map-slice';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || ""

const mapStyles = {
    dark: 'mapbox://styles/spacecowllc-mapbox/cloyr7q7l00ru01qn6hi93i1a',
    light: 'mapbox://styles/spacecowllc-mapbox/cmcc8feaa003e01qseapk39wh'
}

interface IMapBoxMapProps {
    mapLayers: Array<IMapLayer> // Used on initial load to set map layer configs
    mapLayerData: Array<IMapLayerData>  // contains the layer info and data (takes longer to load)
    selectMapPoint: (mapPointData: IMapData, layerid: number) => void
    fetchData: (boundingBox: Array<ICoordinates>) => void,
    mapLoadded: () => void
    featureCollectionSelected: (
        features:  Feature[],
        layer: IMapLayer
    ) => void
    loading: boolean,
    mapLocation?: IMapLocation | null,
    mapSizeChanged: boolean
}

const MapBoxMapComponent = (props: IMapBoxMapProps) => {
    const mapContainer = useRef(null);
    const map = React.useRef<mapboxgl.Map | null>(null);

    const theme = useTheme()
    const dispatch: AppDispatch = useDispatch();

    const [lng, setLng] = useState(-84.2806);
    const [lat, setLat] = useState(30.4382);
    const [zoom, setZoom] = useState(5);

    const [mapLoaded, setMapLoaded] = useState(false)
    const [currentMapStyle, setCurrentMapStyle] = useState(mapStyles.dark)

    useEffect(() => {
        map.current?.resize()
    }, [props.mapSizeChanged])

    // useEffect on component load
    useEffect(() => {
        if(map.current) return

        setMapStyleFromStorage()

        map.current = new mapboxgl.Map({
            container: mapContainer.current || "",
            style: currentMapStyle,
            center: [lng, lat],
            zoom: zoom,
        });

        map.current.on('load', () => {
            setMapLoaded(true)
            props.mapLoadded()

            props.mapLayers.forEach((layer) => {
                setCustomMapMarker(layer)

                const layerSourceId = mapUtils.getMapLayerSourceId(layer)
                const mapLayerConfigs = mapUtils.getMapConfigsForLayer(layer)

                var layerHasClickEvent = false

                if(mapLayerConfigs && layer.map_configs.type === MapLayerTypes.Point){
                    map.current?.on('click', layerSourceId, (e) => {
                        const features = map.current?.queryRenderedFeatures(e.point, {
                            layers: [layerSourceId],
                        });

                        if (features && features.length > 0) {
                            const clickedFeature = features[0];
                            const clickedData = clickedFeature.properties?.data
                            const layerId = mapUtils.getMapLayerIdFromSourceId(layerSourceId)
                            props.selectMapPoint(JSON.parse(clickedData), layerId)
                        }
                    })
                    layerHasClickEvent = true
                } else if (mapLayerConfigs && layer.map_configs.type === MapLayerTypes.Cluster) {
                    map.current?.on('click', layerSourceId, (e) => {
                        const features = map.current?.queryRenderedFeatures(e.point, {
                            layers: [layerSourceId],
                        });

                        if (features && features.length > 0) {
                            const cluster = features[0];
                            const clusterId = cluster.properties?.cluster_id;
                            const source = map.current?.getSource(layerSourceId) as mapboxgl.GeoJSONSource;
                            source.getClusterLeaves(
                                clusterId,
                                Infinity,
                                0,
                                (err, leafFeatures) => {
                                    if (err){
                                        console.error(err)
                                        return
                                    }
                                    
                                    if(leafFeatures)
                                        props.featureCollectionSelected(leafFeatures, layer)
                                }
                            )
                        }
                    })

                    layerHasClickEvent = true
                }

                if (layerHasClickEvent){
                    map.current?.on('mouseenter', layerSourceId, () => {
                        if(map.current)
                            map.current.getCanvas().style.cursor = 'pointer'
                    });
                    map.current?.on('mouseleave', layerSourceId, () => {
                        if(map.current)
                            map.current.getCanvas().style.cursor = ''
                    });
                }
            })

            map.current?.on('move', () => {
                handleMapMove()
            })

            handleMapMove()  // Triggers initial load on loading bounding box

        })

        return () => {  // Cleanup
            handleMapMove.cancel()
        }
    }, []);

    useEffect(() => {

        if(props.mapLocation && map.current && mapLoaded){
            const { lat, lng, zoom, xOffset, yOffset } = props.mapLocation

            map.current?.flyTo({
                center: [lng, lat],
                offset: [(xOffset || 0), (yOffset|| 0) ],
                zoom: zoom || map.current.getZoom(),
                speed: 1.2, 
                curve: 1.42,
                essential: true,
                padding: { top: 50, bottom: 50, left: 50, right: 50 }
            })

        }

    }, [props.mapLocation])

    // Listener for darkmode
    useLocalStorageWatcher(UserSettings.DARKMODE, (newValue) => {
        setMapStyleFromStorage()
    });

    const setMapStyleFromStorage = () => {
        const darkModeValue = localStorage.getItem(UserSettings.DARKMODE)
        if(darkModeValue){
            const isDarkMode =  darkModeValue === 'true'
            if(isDarkMode)
                setCurrentMapStyle(mapStyles.dark)
            else
                setCurrentMapStyle(mapStyles.light)
        }
    }

    useLocalStorageWatcher(UserSettings.MAP_OVERLAP, (newValue) => {
        setMapPointOverlap(newValue === 'true')
    });

    useLocalStorageWatcher(UserSettings.SHOW_MAP_LIST, (newValue) => {
        // resizing map after side bar is hidden
        setTimeout(() => {
            map.current?.resize()
        }, 100)
    });

    const setMapPointOverlap = (overlapValue: boolean) => {
        props.mapLayerData?.forEach((layer) => {
            const layerSourceId = mapUtils.getMapLayerSourceId(layer)
            const mapLayerConfigs = mapUtils.getMapConfigsForLayer(layer)
            if (map.current && mapLayerConfigs && layer.map_configs.type === MapLayerTypes.Point && layerExistsOnMap(layer)){
                map.current.setLayoutProperty(layerSourceId, 'text-allow-overlap', overlapValue)
                map.current.setLayoutProperty(layerSourceId, 'icon-allow-overlap', overlapValue)
            }
        })
    }

    useLocalStorageWatcher(UserSettings.MARKER_TEXT, (newValue) => {
        toggleMarkerText(newValue === 'true')
    });

    const toggleMarkerText = (showText: boolean) => {
        props.mapLayerData?.forEach((layer) => {

            const layerSourceId = mapUtils.getMapLayerSourceId(layer)
            if(map.current && layer.map_configs.type == MapLayerTypes.Point){
                let textField = ""
                const mapLayerConfigs = mapUtils.getMapConfigsForLayer(layer)
                if(showText && mapLayerConfigs && mapLayerConfigs[0]?.layout 
                    && !Array.isArray(mapLayerConfigs[0].layout['text-field']))
                    textField = mapLayerConfigs[0].layout['text-field']

                if (layerExistsOnMap(layer))
                    map.current.setLayoutProperty(layerSourceId, 'text-field', textField)
            }
        })
    }

    const setCustomMapMarker = (layer: IMapLayer) => {
        const layerSourceId = mapUtils.getMapLayerSourceId(layer)
        if (map.current && !map.current.hasImage(layerSourceId) && layer.map_configs.type == MapLayerTypes.Point)
            var iconImage = layer.map_configs.icon_image
            if (!iconImage)
                iconImage = 'default_map_marker.svg'

            fetch(`${process.env.PUBLIC_URL}/assets/${iconImage}`)
            .then(async res => {
                const svgText = await res.text();
                return'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);
            }).then((imageUri) => {
                const image = new Image(40,40)
                image.src = imageUri
                if(!map.current?.hasImage(layerSourceId))
                    image.onload = () => map.current?.addImage(layerSourceId, image, { sdf: true })
            }).catch((e) =>{

            })
    }

    useEffect(() => {
        if(map.current){
            map.current.setStyle(currentMapStyle)
            map.current.once('style.load', () => {
                addAllLayersToMap()
            });
        }
    }, [currentMapStyle])

    const layerExistsOnMap = (layer: IMapLayer) => {
        const configs = mapUtils.getMapConfigsForLayer(layer)
        let onMap = false
        configs?.forEach((config) => {
            if (!onMap)
                onMap = !!map?.current?.getLayer(config.id)
        })
        return onMap
    }

    const addFeatureLayerToMap = (mapDataLayer: IMapLayerData) => {
        const addressMapPoints = mapUtils.convertAddressToMapPoint(mapDataLayer)
        dispatch(setLayerFeatureCollection({layerId: mapDataLayer.id, features: addressMapPoints.features}))
        toggleLayer(addressMapPoints, mapDataLayer)
    }

    const toggleLayer = (
        data: FeatureCollection<Polygon> | GeoJSONFeatureCollection,
        mapDataLayer: IMapLayerData
    ) => {
        const layerSourceId = mapUtils.getMapLayerSourceId(mapDataLayer)
        const currentDataSource = map.current?.getSource(layerSourceId) as GeoJSONSource
        if(currentDataSource){  // If the data source is already on the map, update the data 
            currentDataSource.setData(data) 

            // Resetting visibility
            if(layerExistsOnMap(mapDataLayer)){
                const mapConfigs = mapUtils.getMapConfigsForLayer(mapDataLayer)

                mapConfigs?.forEach((config) => {
                    map.current?.setLayoutProperty(config.id, 'visibility', 'visible');
                })
            }
        } else {  // If the data source doesn't exist, create it
            map.current?.addSource(layerSourceId, {
                type: 'geojson',
                data: data,
                cluster: mapDataLayer.map_configs.type === MapLayerTypes.Cluster,
                clusterMaxZoom: 14, 
                clusterRadius: 50 
            })    

            const mapLayerConfigs = mapUtils.getMapConfigsForLayer(mapDataLayer) 
            if (!mapLayerConfigs) return
            
            mapLayerConfigs.forEach((config: Record<string, any>) => {
                if(config && config?.paint && 'text-color' in config.paint){
                    config.paint['text-color'] = theme.palette.text.primary
                }
                
                map.current?.addLayer(config as any)
            })
        }

        if (map.current){
            const mapOverlapValue = localStorage.getItem(UserSettings.MAP_OVERLAP)
            if(mapOverlapValue)
                setMapPointOverlap(mapOverlapValue === 'true')

            const showMakerTextValue = localStorage.getItem(UserSettings.MARKER_TEXT)
            if(showMakerTextValue)
                toggleMarkerText(showMakerTextValue === 'true')
        }
    }

    const addChoroplethLayerToMap = (mapDataLayer: IMapLayerData) => {
        const choroplethMapPolygons = mapUtils.convertChoroplethDataToPolygon(mapDataLayer.data)
        dispatch(setLayerFeatureCollection({layerId: mapDataLayer.id, features: choroplethMapPolygons.features}))
        const choroplethMapCenters = mapUtils.getChoroplethPolygonCenter(mapDataLayer.data)
        const layerSourceId = mapUtils.getMapLayerSourceId(mapDataLayer)
        const centerLayerSourceId = `${layerSourceId}-center`
        const currentDataSource = map.current?.getSource(centerLayerSourceId) as GeoJSONSource
        if(currentDataSource){
            currentDataSource.setData(choroplethMapCenters) 
        } else {
            map.current?.addSource(centerLayerSourceId, {
                type: 'geojson',
                data: choroplethMapCenters,
                cluster: mapDataLayer.map_configs.type === MapLayerTypes.Cluster,
                clusterMaxZoom: 14, 
                clusterRadius: 50 
            })    
        }

        toggleLayer(choroplethMapPolygons, mapDataLayer)
    }

    const addAllLayersToMap = () => {
        // Putting choropleth first so they are rendered on the bottom
        const reorderedLayers = [
            ...props.mapLayerData.filter((x) => x.map_configs.type === MapLayerTypes.Choropleth),
            ...props.mapLayerData.filter((x) => x.map_configs.type !== MapLayerTypes.Choropleth)
        ]
        reorderedLayers.forEach((layer) => {
            const foundLayer = props.mapLayers.find((x) => x.id === layer.id)
            setCustomMapMarker(layer)
            if (foundLayer?.visible){
                if(layer.map_configs.type !== MapLayerTypes.Choropleth)
                    addFeatureLayerToMap(layer)
                else
                    addChoroplethLayerToMap(layer)
            }
            else {
                removeLayerFromMap(layer)
            }
        })
    }

    useEffect(() => {
        if(mapLoaded && props.mapLayerData){
            addAllLayersToMap()
        }
    }, [props.mapLayerData])

    const handleMapMove = useMemo(
        () =>
            debounce(() => {
                const bounds = map.current?.getBounds()
                if (bounds){
                    const boundingBox = mapUtils.convertBoundingBox(bounds)
                    props.fetchData(boundingBox)
                }
            }, 500),
        []
    );

    const removeLayerFromMap = (mapLayer: IMapLayerData) => {
        const layerSourceId = mapUtils.getMapLayerSourceId(mapLayer)
        if (!layerSourceId) return
        const mapConfigs = mapUtils.getMapConfigsForLayer(mapLayer)
        mapConfigs?.forEach((config) => {
            if (config && map.current?.getLayer(config.id)){
                map.current?.setLayoutProperty(config.id, 'visibility', 'none');
            }
        })
    }

    return (
        <div className='map-wrapper'>
            <div ref={mapContainer} className="map" />
        </div>
    )
}

export default MapBoxMapComponent